import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, CheckCircle2, Loader2, RefreshCw, Undo2 } from 'lucide-react';
import type { FxRevaluationPreview, RevaluationPosition } from '@polatkan/api/services';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { trpc } from '@/lib/trpc';
import { cn, formatDate } from '@/lib/utils';
import { useAppStore } from '@/stores/app-store';
import { showErrorToast, showSuccessToast } from '@/hooks/use-mutation-with-toast';
import { Tag } from '@/components/accounting/posting-rules/status-badge';
import { Difference, Money, Stat } from '@/components/accounting/tie-out/money';
import { entryHref, isoDay, utcDate, type TieOutApp } from '@/components/accounting/tie-out/model';

const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;

/** Last day of the previous month (UTC) — the usual valuation date. */
export function defaultValuationDay(today: Date): string {
  return isoDay(new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 0)));
}

export function positionIsPostable(p: Pick<RevaluationPosition, 'warning' | 'difference'>): boolean {
  return p.warning === null && Math.abs(p.difference) >= 0.01;
}

interface FxRevaluationTabProps {
  app: TieOutApp;
}

export function FxRevaluationTab({ app }: FxRevaluationTabProps) {
  const { t } = useTranslation('periodEnd');
  const today = useMemo(() => new Date(), []);
  const [draft, setDraft] = useState(() => defaultValuationDay(today));
  const [asOf, setAsOf] = useState(draft);
  const [reverseNextDay, setReverseNextDay] = useState(true);
  const [voidReason, setVoidReason] = useState('');
  const [confirmVoid, setConfirmVoid] = useState(false);

  const canEdit = useAppStore((s) => s.hasPermissionForApp(app, 'ACCOUNTING', 'EDIT'));
  const utils = trpc.useUtils();
  const input = useMemo(() => ({ app, asOf: utcDate(asOf) }), [app, asOf]);
  const preview = trpc.periodEnd.fxPreview.useQuery(input, { enabled: DAY_RE.test(asOf) });
  const invalidate = () => utils.periodEnd.fxPreview.invalidate(input);
  const post = trpc.periodEnd.fxPost.useMutation({
    onSuccess: (r) => {
      showSuccessToast(t('fx.toast.posted', { gain: r.totals.gain, loss: r.totals.loss }));
      invalidate();
    },
    onError: (e) => showErrorToast(e.message),
  });
  const voidMutation = trpc.periodEnd.fxVoid.useMutation({
    onSuccess: (r) => {
      showSuccessToast(t('fx.toast.voided', { count: r.voided }));
      setConfirmVoid(false);
      setVoidReason('');
      invalidate();
    },
    onError: (e) => showErrorToast(e.message),
  });

  const data: FxRevaluationPreview | undefined = preview.data;
  const activeEntries = data?.entries.filter((e) => e.status === 'POSTED') ?? [];
  const busy = post.isPending || voidMutation.isPending;

  return (
    <div className="space-y-6">
      <form
        className="flex flex-wrap items-end gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (DAY_RE.test(draft)) setAsOf(draft);
        }}
      >
        <label className="text-xs text-muted-foreground">
          {t('fx.asOf')}
          <Input type="date" value={draft} onChange={(e) => setDraft(e.target.value)} className="mt-1 h-8 w-[160px]" />
        </label>
        <Button type="submit" variant="outline" className="h-8" disabled={!DAY_RE.test(draft) || preview.isFetching}>
          {preview.isFetching ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
          {t('fx.apply')}
        </Button>
      </form>

      {preview.isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      )}
      {preview.error && <div className="border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">{preview.error.message}</div>}

      {data && (
        <>
          {/* Rates + totals */}
          <div className="grid gap-2 sm:grid-cols-3 lg:grid-cols-6">
            <Stat label={t('fx.totals.gain')} value={<Money value={data.totals.gain} />} tone={data.totals.gain > 0 ? 'emerald' : 'neutral'} />
            <Stat label={t('fx.totals.loss')} value={<Money value={data.totals.loss} />} tone={data.totals.loss > 0 ? 'red' : 'neutral'} />
            <Stat label={t('fx.totals.net')} value={<Money value={data.totals.net} />} />
            <Stat label={t('fx.totals.included')} value={data.totals.included} />
            <Stat label={t('fx.totals.excluded')} value={data.totals.excluded} tone={data.totals.excluded > 0 ? 'amber' : 'neutral'} />
            <Stat
              label={t('fx.rates')}
              value={
                data.rates.length === 0 ? (
                  <span className="text-muted-foreground">—</span>
                ) : (
                  <span className="flex flex-wrap gap-1 font-mono text-xs">
                    {data.rates.map((r) => (
                      <span key={r.currency} className={cn(r.rate == null && 'text-red-700 dark:text-red-300')}>
                        {r.currency} {r.rate ?? '?'}
                      </span>
                    ))}
                  </span>
                )
              }
            />
          </div>

          {/* Entries for this date + actions */}
          <section className="rounded-md border border-border">
            <header className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-4 py-3">
              <div>
                <h2 className="text-base font-semibold">{t('fx.entries.title', { date: formatDate(data.asOf) })}</h2>
                <p className="text-xs text-muted-foreground">{t('fx.prefixes', { list: data.accountPrefixes.join(', ') })}</p>
              </div>
              {canEdit ? (
                activeEntries.length === 0 ? (
                  <div className="flex flex-wrap items-center gap-3">
                    <label className="flex items-center gap-2 text-xs">
                      <input type="checkbox" checked={reverseNextDay} onChange={(e) => setReverseNextDay(e.target.checked)} disabled={busy} />
                      {t('fx.reverseNextDay')}
                    </label>
                    <Button size="sm" className="h-8" disabled={!data.canPost || busy} onClick={() => post.mutate({ ...input, reverseNextDay })}>
                      {post.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-2 h-4 w-4" />}
                      {post.isPending ? t('fx.posting') : t('fx.post')}
                    </Button>
                  </div>
                ) : confirmVoid ? (
                  <div className="flex flex-wrap items-center gap-2">
                    <Input
                      value={voidReason}
                      onChange={(e) => setVoidReason(e.target.value)}
                      placeholder={t('fx.voidReason')}
                      className="h-8 w-[240px]"
                      disabled={busy}
                    />
                    <Button size="sm" variant="destructive" className="h-8" disabled={voidReason.trim().length < 3 || busy} onClick={() => voidMutation.mutate({ ...input, reason: voidReason.trim() })}>
                      {voidMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Undo2 className="mr-2 h-4 w-4" />}
                      {t('fx.voidConfirm')}
                    </Button>
                    <Button size="sm" variant="ghost" className="h-8" disabled={busy} onClick={() => setConfirmVoid(false)}>
                      {t('fx.voidCancel')}
                    </Button>
                  </div>
                ) : (
                  <Button size="sm" variant="outline" className="h-8" disabled={busy} onClick={() => setConfirmVoid(true)}>
                    <Undo2 className="mr-2 h-4 w-4" />
                    {t('fx.void')}
                  </Button>
                )
              ) : (
                <span className="text-xs text-muted-foreground">{t('fx.readOnly')}</span>
              )}
            </header>

            {data.entries.length === 0 ? (
              <p className="px-4 py-3 text-sm text-muted-foreground">{data.canPost ? t('fx.entries.none') : t('fx.nothingToPost')}</p>
            ) : (
              <ul className="divide-y divide-border">
                {data.entries.map((e) => (
                  <li key={e.id} className="flex flex-wrap items-center gap-3 px-4 py-2 text-sm">
                    <Link to={entryHref(app, e.id)} className="font-mono text-xs hover:underline">
                      {e.entryNumber}
                    </Link>
                    <span className="text-muted-foreground">{formatDate(e.date)}</span>
                    <Tag>{t(`fx.entries.kind.${e.kind}`)}</Tag>
                    <Tag className={e.status === 'POSTED' ? 'text-emerald-700 dark:text-emerald-300' : 'text-red-700 dark:text-red-300'}>
                      {t(`fx.entries.status.${e.status}`, { defaultValue: e.status })}
                    </Tag>
                  </li>
                ))}
              </ul>
            )}
            {activeEntries.length > 0 && (
              <p className="border-t border-border px-4 py-2 text-xs text-muted-foreground">{t('fx.alreadyPosted')}</p>
            )}
          </section>

          {/* Positions */}
          <section className="rounded-md border border-border">
            <header className="border-b border-border px-4 py-3">
              <h2 className="text-base font-semibold">{t('fx.positionsTitle')}</h2>
              <p className="text-xs text-muted-foreground">{t('fx.positionsDesc')}</p>
            </header>
            {data.positions.length === 0 ? (
              <p className="px-4 py-4 text-sm text-muted-foreground">{t('fx.noForeignBalances')}</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                      <th className="px-3 py-2 font-medium">{t('fx.columns.account')}</th>
                      <th className="px-3 py-2 font-medium">{t('fx.columns.currency')}</th>
                      <th className="px-3 py-2 font-medium">{t('fx.columns.party')}</th>
                      <th className="px-3 py-2 text-right font-medium">{t('fx.columns.fxBalance')}</th>
                      <th className="px-3 py-2 text-right font-medium">{t('fx.columns.bookBalance')}</th>
                      <th className="px-3 py-2 text-right font-medium">{t('fx.columns.rate')}</th>
                      <th className="px-3 py-2 text-right font-medium">{t('fx.columns.revalued')}</th>
                      <th className="px-3 py-2 text-right font-medium">{t('fx.columns.difference')}</th>
                      <th className="px-3 py-2 font-medium">{t('fx.columns.status')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.positions.map((p) => (
                      <tr key={`${p.accountId}|${p.currency}|${p.party?.id ?? ''}`} className={cn('border-b border-border last:border-b-0', p.warning && 'bg-amber-50/50 dark:bg-amber-900/10')}>
                        <td className="px-3 py-2 whitespace-nowrap">
                          <span className="font-mono text-xs">{p.accountCode}</span> <span className="text-muted-foreground">{p.accountName}</span>
                        </td>
                        <td className="px-3 py-2 font-mono text-xs">{p.currency}</td>
                        <td className="px-3 py-2">{p.party ? p.party.name : <span className="text-muted-foreground">—</span>}</td>
                        <td className="px-3 py-2 text-right">
                          <Money value={p.fxBalance} currency={p.currency} />
                        </td>
                        <td className="px-3 py-2 text-right">
                          <Money value={p.bookBalance} />
                        </td>
                        <td className="px-3 py-2 text-right font-mono text-xs">{p.rate ?? '—'}</td>
                        <td className="px-3 py-2 text-right">{p.revaluedBalance == null ? '—' : <Money value={p.revaluedBalance} />}</td>
                        <td className="px-3 py-2 text-right">{p.warning ? <Money value={p.difference} className="text-muted-foreground" /> : <Difference value={p.difference} />}</td>
                        <td className="px-3 py-2">
                          {p.warning ? (
                            <Tag className="text-amber-700 dark:text-amber-300">
                              <AlertTriangle className="mr-1 h-3 w-3" />
                              {t(`fx.warning.${p.warning}`)}
                            </Tag>
                          ) : positionIsPostable(p) ? (
                            <Tag className="text-emerald-700 dark:text-emerald-300">{t('fx.willPost')}</Tag>
                          ) : (
                            <Tag>{t('fx.immaterial')}</Tag>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}
